import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { OrderManagerService, eQCFormType, SigningLocationComponent, ValidationService, Order, Amendment, eAmendmentType, ConfigurationState, ValidationProblem, eOrderType } from 'reg-hub-common';
import { BaseLienDetailsComponent } from '../base-lien-details.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { formatNumber } from '@angular/common';

@Component({
    selector: 'app-qc-lien-details',
    templateUrl: './qc-lien-details.component.html',
    styleUrls: ['./qc-lien-details.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QCLienDetailsComponent extends BaseLienDetailsComponent {
  @ViewChild(SigningLocationComponent) signingLocationComponent!: SigningLocationComponent;

  amendmentTypeError$ = new BehaviorSubject<string | null | undefined>(null);
  sumOfHypothecError$ = new BehaviorSubject<string | null | undefined>(null);

  change = eAmendmentType.change;
  correction = eAmendmentType.correction;

  protected isRHa: boolean = false;

  private orderToValidate: Order | null = null;

  constructor(
    formBuilder: FormBuilder,
    orderManager: OrderManagerService,
    validationService: ValidationService,
    private configurationState: ConfigurationState) {
    super(formBuilder, orderManager, validationService);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.uiConfiguration.lienDetailsConfiguration?.showUseDefaultSumOfHypothec &&
      (this.order.lien?.useDefaultSumOfHypothec ?? true)) {
      this.lienDetails.get('sumOfHypothec')?.disable();
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showUseDefaultSumOfHypothec) {
      this.lienDetails.get('amount')?.valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(amount => this.calculateSumOfHypothec(amount));

      this.calculateSumOfHypothec(this.order.lien?.amount);
    } else if (this.order.referenceRegistrationInformation?.originalQCFormTypeID === eQCFormType.RHa) {
      this.lienDetails.get('amendmentTypeID')?.valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(val => {
          const amountControl = this.lienDetails.get('amount');
          const sumOfHypothecControl = this.lienDetails.get('sumOfHypothec');

          if (val === eAmendmentType.change) {
            amountControl?.enable({ emitEvent: false });

            const sumOfHypothecValue = this.order.orderChanges?.find(c => c.path.toLowerCase().endsWith('sumofhypothec'))?.originalValue ??
              this.order.lien?.sumOfHypothec;

            sumOfHypothecControl?.setValue(sumOfHypothecValue, { emitEvent: false });
            sumOfHypothecControl?.disable({ emitEvent: false });
          } else if (val === eAmendmentType.correction) {
            let amountValue = this.order.orderChanges?.find(c => c.path.toLowerCase().endsWith('amount'))?.originalValue ??
              this.order.lien?.amount;

            //If we end up getting the new value from an order change object it is a string and may have a bunch of trailing
            //zeroes past the decimal, we'd only ever want to show up to two of these.
            if (typeof amountValue === 'string' && amountValue !== undefined && amountValue !== null) {
              amountValue = formatNumber(+(amountValue), 'en-US', '1.0-2').replace(',', '');
            }

            amountControl?.setValue(amountValue, { emitEvent: false });
            amountControl?.disable({ emitEvent: false });
            sumOfHypothecControl?.enable({ emitEvent: false });
          }
        });
    }
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    if (this.uiConfiguration.lienDetailsConfiguration?.showSigningCountry) {
      this.lienDetails.setControl('signingCountry', this.signingLocationComponent.signingLocation.get('signingCountry'), { emitEvent: false });
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showSigningJurisdiction) {
      this.lienDetails.setControl('signingJurisdiction', this.signingLocationComponent.signingLocation.get('signingJurisdiction'), { emitEvent: false });
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showSigningCity) {
      this.lienDetails.setControl('signingCity', this.signingLocationComponent.signingLocation.get('signingCity'), { emitEvent: false });
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showSigningDate) {
      this.lienDetails.setControl('signingDate', this.signingLocationComponent.signingLocation.get('signingDate'), { emitEvent: false });
    }
  }

  updateSigningLocation(order: Order) {
    if (this.signingLocationComponent) {
      order.lien!.signingDate = this.signingLocationComponent.signingDate;
      order.lien!.signingCity = this.signingLocationComponent.signingCity;
      order.lien!.signingJurisdiction = this.signingLocationComponent.signingJurisdiction;
      order.lien!.signingCountry = this.signingLocationComponent.signingCountry;
    }
  }

  useDefaultSumOfHypothecChanged() {
    if (this.order.lien!.useDefaultSumOfHypothec ?? true) {
      this.order.lien!.useDefaultSumOfHypothec = false;
      this.lienDetails.get('sumOfHypothec')?.enable();
      this.order.lien!.sumOfHypothec = this.lienDetails.get('sumOfHypothec')?.value;
    } else {
      this.order.lien!.useDefaultSumOfHypothec = true;
      this.lienDetails.get('sumOfHypothec')?.disable();
      let amount = this.lienDetails.get('amount')?.value;
      this.calculateSumOfHypothec(amount);
    }
  }

  calculateSumOfHypothec(amount: any) {
    if (this.isRHa && (this.order.lien?.useDefaultSumOfHypothec ?? true)) {
      if (amount !== null && !isNaN(this.order.lien!.amount!)) {
        let sumOfHypothec: string | null = null;
        const amountAsNumber = Number(amount);
        let sumOfHypothecAmount = amount;

        this.configurationState.getDefaultSumOfHypothecText().then(text => {
          if (text) {
            this.configurationState.getDefaultSumOfHypothecPercent().then(percent => {
              let percentString = "";
              if (percent) {
                sumOfHypothecAmount = amountAsNumber + (amountAsNumber * percent);
                percentString = (percent * 100).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              }

              sumOfHypothec = text
                .replaceAll("[[Amount]]", amountAsNumber.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                .replaceAll("[[AmountWithPercent]]", sumOfHypothecAmount.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                .replaceAll("[[Percent]]", `${percentString} %`);

              this.lienDetails.get('sumOfHypothec')!.setValue(sumOfHypothec);
            });
          } else {
            //Use default text
            sumOfHypothecAmount = amountAsNumber * 1.2;

            //MARK: If Sum of Hypothec wording changes in the GenerateSumOfHypothec step in the API, this will also need to be updated.
            sumOfHypothec = `${amountAsNumber.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ CAD avec intérêts de au taux de 20% l'an et incluant une hypothèque additionnelle de ${sumOfHypothecAmount.toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $ (20%) CAD.`;

            this.lienDetails.get('sumOfHypothec')!.setValue(sumOfHypothec);
          }
        });
      }
    }
  }

  handleSigningLocationFormValueChangedEvent() {
    this.updateSigningLocation(this.order);

    if (!this.orderToValidate) {
      this.orderToValidate = this.copyOrder(this.order);
    }

    if (this.order.orderTypeID === eOrderType.amendment) {
      this.order.amendment = {
        amendmentTypeID: this.lienDetails.get('amendmentTypeID')?.value
      };
    }

    if (this.isInitialized) {
      this.validate(this.orderToValidate!);
    }
  }

  override onSaving(): void {
    super.onSaving();

    this.updateSigningLocation(this.order);

    if (this.uiConfiguration.amendmentConfiguration?.showCorrectionOrChange) {
      this.order.amendment = {
        amendmentTypeID: this.lienDetails.get('amendmentTypeID')?.value
      };
    }
  }

  override pushErrors(errors: ValidationProblem[] | undefined): void {
    if (!errors) {
      return;
    }

    super.pushErrors(errors);
    this.amendmentTypeError$.next(errors.filter(error => error.path.includes('/amendmenttypeid')).at(0)?.userFriendlyMessage);
    this.sumOfHypothecError$.next(errors.filter(error => error.path.includes('/sumofhypothec')).at(0)?.userFriendlyMessage);
  }

  protected override buildForm(): FormGroup {
    var form = this.formBuilder.group({});
    this.isRHa = this.order.lien?.qcFormTypeID === eQCFormType.RHa;

    if (this.uiConfiguration.amendmentConfiguration?.showCorrectionOrChange) {
      form.addControl('amendmentTypeID', this.formBuilder.control(this.order.amendment?.amendmentTypeID ?? eAmendmentType.change));
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showAmount) {
      form.addControl('amount', this.formBuilder.control(this.order.lien?.amount));
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showSumOfHypothec) {
      const sumOfHypothecControl = this.formBuilder.control(this.order.lien?.sumOfHypothec);
      const amendmentTypeControl = form.get('amendmentTypeID');

      if (this.uiConfiguration.amendmentConfiguration?.showCorrectionOrChange && amendmentTypeControl) {
        const amountControl = form.get('amount');

        if (amendmentTypeControl?.value === eAmendmentType.change) {
          amountControl?.enable({ emitEvent: false });
          sumOfHypothecControl.disable({ emitEvent: false });
        } else if (amendmentTypeControl?.value === eAmendmentType.correction) {
          amountControl?.disable({ emitEvent: false });
          sumOfHypothecControl.enable({ emitEvent: false });
        }
      }

      form.addControl('sumOfHypothec', sumOfHypothecControl);
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showUseDefaultSumOfHypothec) {
      form.addControl('useDefaultSumOfHypothec', this.formBuilder.control(this.order.lien?.useDefaultSumOfHypothec ?? true));
    }

    if (this.uiConfiguration.lienDetailsConfiguration?.showAdditionalInformation) {
      form.addControl('additionalInformation', this.formBuilder.control(this.order.lien?.additionalInformation));
    }

    return form;
  }

  protected shouldAllowDefaultSumOfHypothec() {
    const amendmentType = this.lienDetails.get('amendmentTypeID')?.value;
    return this.uiConfiguration.amendmentConfiguration?.showCorrectionOrChange ? this.isRHa && amendmentType !== eAmendmentType.change : this.isRHa;
  }

  protected onCopyAmount() {
    const amountControl = this.lienDetails.get('amount');
    const sumOfHypothecControl = this.lienDetails.get('sumOfHypothec');

    if (amountControl && sumOfHypothecControl) {
      sumOfHypothecControl.setValue(amountControl.value ?? '');
    }
  }

  protected shouldDisableCopyAmount() {
    const useDefaultSumOfHypothecControl = this.lienDetails.get('useDefaultSumOfHypothec');

    if (!useDefaultSumOfHypothecControl) {
      return true;
    } else {
      return useDefaultSumOfHypothecControl.value === true;
    }
  }

  protected shouldDisableSumOfHypothecTooltip() {
    const useDefaultSumOfHypothecControl = this.lienDetails.get('useDefaultSumOfHypothec');

    if (!useDefaultSumOfHypothecControl) {
      return true;
    } else {
      return !useDefaultSumOfHypothecControl.value;
    }
  }
}

